import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  combineLatest,
  forkJoin,
} from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { ProjectExpensesService } from '../project-expenses.service';
import {
  Project,
  InvoiceData,
  Versioning,
  ProjectManagerService,
  BankAccountService,
  BankAccount,
  InvoicePostRequest,
  DataSyncService,
} from '@ops/shared-types';
import { Miscellanious } from '@ops/global-config';

@Component({
  selector: 'app-estimates-and-invoices',
  templateUrl: './estimates-and-invoices.component.html',
  styleUrls: ['./estimates-and-invoices.component.scss'],
})
export class EstimatesAndInvoicesComponent implements OnInit, OnDestroy {
  @Input() set project(project: Project) {
    this._project = project;
    this.loadData(project);
  }
  @Input() isInPayments = false;
  isInvoiceAddDialogVisible$ = new BehaviorSubject<boolean>(false);
  projectInvoicesData$: Observable<{
    invoices: InvoiceData[];
    versioning: Versioning;
  }>;
  bankAccounts$: Observable<string[]>;
  dataRefetchBehaviorSubject$: BehaviorSubject<number>;
  dataRefetchSub: Subscription;

  mockDate = new Date();

  showingArchived: boolean = false;

  _project: Project;

  projectTotal: number;
  projectFee: number;
  projectTax: number;
  projectCurrency: string;

  bankAccounts: Array<{ id: string; name: string; currencyCode?: string }>;
  isLoading: boolean = true;
  subscriptions: Array<Subscription> = [];

  constructor(
    private projectExpensesService: ProjectExpensesService,
    private projectService: ProjectManagerService,
    private bankAccountService: BankAccountService,
    private dataSyncService: DataSyncService
  ) {
    this.projectFee =
      this.projectService.getCurrentProject()?.feePercentage ?? 0;
    this.projectTax =
      this.projectService.getCurrentProject()?.taxPercentage ?? 0;
    this.projectCurrency =
      this.projectService.getCurrentProject()?.projectCurrency ?? '';
    this.projectTotal =
      Math.round(
        (this.projectService.getCurrentProject()?.total ?? 0) *
          (1 + this.projectFee / 100 + this.projectTax / 100) *
          100
      ) / 100;

    this.dataRefetchBehaviorSubject$ =
      this.dataSyncService.getRefetchVersionListener();
  }
  ngOnInit(): void {
    this.dataRefetchSub = this.dataRefetchBehaviorSubject$.subscribe(() => {
      // console.log('DATA SYNC TRIGGERED UPDATE');

      if (this._project) {
        this.loadData(this._project);
      }
    });
  }

  showArchived(archived: boolean) {
    this.showingArchived = archived;
  }

  loadData(project: Project) {
    this.isLoading = true;
    this.projectInvoicesData$ = combineLatest([
      this.bankAccountService
        .getAllBankAccountsForCompany(project.company ?? '')
        .pipe(
          switchMap((accounts) => {
            this.bankAccounts = accounts
              .filter(
                (a) =>
                  !['DEBIT', 'PETTY CASH'].includes(a.bankName?.toUpperCase())
              )
              .map((a: BankAccount) => {
                return {
                  id: a._id,
                  name: `${a.bankName} ${a.currencyCode}`,
                  currencyCode: a.currencyCode,
                };
              });

            return forkJoin({
              activeInvoices: this.projectExpensesService.getInvoices(
                project.versionLink ?? ''
              ),
              archivedInvoices: this.projectExpensesService.getArchivedInvoices(
                project.versionLink ?? ''
              ),
            });
          })
        )
        .pipe(
          map(({ activeInvoices, archivedInvoices }) => {
            const processInvoice = (invoice: any) => {
              const splittedSender = invoice.sentBy.split(/[-\s]/);
              if (splittedSender.length > 1) {
                invoice.sentBy = splittedSender[0][0] + splittedSender[1][0];
              }

              const account = this.bankAccounts.find(
                (a) => a.id === invoice.bankAccount
              );
              invoice.bankAccount = account?.name;
              invoice.currencyCode = account?.currencyCode;

              return invoice;
            };

            return {
              activeInvoices: activeInvoices.map(processInvoice),
              archivedInvoices: archivedInvoices.map(processInvoice),
            };
          })
        ),
      this.projectExpensesService.getVersioning(project._id),
    ]).pipe(
      map(([invoicesData, versioning]) => ({
        invoices: invoicesData.activeInvoices,
        archivedInvoices: invoicesData.archivedInvoices,
        versioning,
        project,
      })),
      tap(() => {
        this.isLoading = false;
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.dataRefetchSub?.unsubscribe();
  }

  saveInvoice(project: Project, data: InvoicePostRequest) {
    // console.log(data);
    const sub = this.projectExpensesService
      .createInvoice(project._id, data)
      .subscribe((data) => {
        // console.log(data);
        this.toggleInvoiceAddDialog(false);
        this.loadData(project);
      });
    this.subscriptions.push(sub);
  }

  toggleInvoiceAddDialog(value: boolean) {
    this.bankAccounts$ = this.projectExpensesService.getBankAccounts().pipe(
      map((bankAccounts) => {
        return bankAccounts.map(
          (bankAccount) => bankAccount.bankName + ' ' + bankAccount.currencyCode
        );
      })
    );
    this.isInvoiceAddDialogVisible$.next(value);
  }

  removeInvoice(invoiceId: string) {
    this.loadData(this._project);
  }

  get projectBaseCurrency() {
    return Miscellanious.currencySymbolFromCode('en-US', this.projectCurrency);
  }
}
